import { safeInject } from '@wui/common-library'
import {
  HistoryMessageItem,
  isMessageNotReactionOrNotification,
  MessageCallReceived,
} from '@wui/im'
import { Emitter } from 'mitt'
import { nextTick, onScopeDispose, ref } from 'vue'

import { chatModel } from '@/entities/messages'

import { Events } from '@/shared/types/app'

import { useChatHistoryLoading } from './use-chat-history-loading'

/** Небольшая пачка: быстрее ответ и меньше скачок при вставке вверх */
export const LAZY_LOAD_HISTORY_BATCH_SIZE = 15

/** Лимит цепочки подгрузок, если сервер отдаёт только невидимые в ленте события */
const MAX_INVISIBLE_HISTORY_CHAIN = 5

type UseChatLazyLoadHooks = {
  /** When false — skip load (user at bottom / scrolling down). */
  canLoadOlderHistory?: () => boolean
  /** Before network/insert — lock scroll, capture viewport anchor. */
  beforeHistoryLoad?: () => void | Promise<void>
  /**
   * Sync, immediately before addMessages.
   * Enable virtua `shift` here — not earlier (realtime appends would corrupt offset).
   */
  preparePrepend?: () => void
  /** Once after addMessages (length change scheduled). */
  onHistoryAppended?: () => void
  afterHistoryLoad?: () => void | Promise<void>
  /** После cooldown — повторная проверка, если пользователь всё ещё у верхней границы */
  onLoadCooldownEnd?: () => void
}

const isDisplayableChatEvent = (event: HistoryMessageItem): boolean => {
  return isMessageNotReactionOrNotification(event)
    && (event.item as MessageCallReceived).event !== 'call_record_completed'
}

export const useChatLazyLoad = (
  pServed: string,
  disableHistoryLoading = false,
  hooks?: UseChatLazyLoadHooks,
) => {
  const { loading, startLoading, stopLoading } = useChatHistoryLoading()
  const {
    findRoomMessages,
    fetchHistory,
    addMessages,
    updateViewportAfterOlderLoad,
    hasMoreOlderHistory,
    findById,
  } = chatModel.useChatMessageStore()

  const emitter = safeInject<Emitter<Events>>('emitter')
  const isEnabled = ref(false)
  const coolDown = ref(false)
  let coolDownTimer: ReturnType<typeof setTimeout> | null = null
  let pendingHistory: HistoryMessageItem[] | null = null
  let prefetchPromise: Promise<void> | null = null
  let isDisposed = false
  let isLoadInFlight = false

  const countDisplayableMessages = (): number => {
    return findRoomMessages(pServed).filter(isDisplayableChatEvent).length
  }

  const hasMoreHistory = (): boolean => hasMoreOlderHistory(pServed)

  const countNewHistoryMessages = (messages: HistoryMessageItem[]): number => {
    return messages.filter((message) => message.eventId && !findById(message.eventId)).length
  }

  const clearPendingHistory = (): void => {
    pendingHistory = null
    prefetchPromise = null
  }

  const appendHistory = (messages: HistoryMessageItem[]): void => {
    if (messages.length) {
      addMessages(pServed, messages)
      updateViewportAfterOlderLoad(pServed, messages, LAZY_LOAD_HISTORY_BATCH_SIZE)
    }
  }

  const prefetchEvents = (): void => {
    if (
      isDisposed
      || disableHistoryLoading
      || loading.value
      || coolDown.value
      || prefetchPromise
      || pendingHistory?.length
      || !hasMoreHistory()
      || (hooks?.canLoadOlderHistory && !hooks.canLoadOlderHistory())
    ) {
      return
    }

    prefetchPromise = fetchHistory(pServed, LAZY_LOAD_HISTORY_BATCH_SIZE)
      .then((history) => {
        if (history.length) {
          pendingHistory = history
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        prefetchPromise = null
      })
  }

  const startCoolDown = (onComplete?: () => void): void => {
    coolDown.value = true
    if (coolDownTimer) {
      clearTimeout(coolDownTimer)
    }
    coolDownTimer = setTimeout(() => {
      coolDown.value = false
      coolDownTimer = null
      onComplete?.()
    }, 800)
  }

  const loadEvents = () => {
    if (
      isDisposed
      || isLoadInFlight
      || loading.value
      || disableHistoryLoading
      || coolDown.value
      || !hasMoreHistory()
      || (hooks?.canLoadOlderHistory && !hooks.canLoadOlderHistory())
    ) {
      return
    }

    isLoadInFlight = true
    const loadHistory = async (invisibleChain = 0): Promise<void> => {
      if (prefetchPromise) {
        await prefetchPromise
      }
      if (isDisposed) {
        return
      }

      if (hooks?.canLoadOlderHistory && !hooks.canLoadOlderHistory()) {
        clearPendingHistory()
        return
      }

      const cached = pendingHistory
      pendingHistory = null
      const needsNetwork = !cached?.length
      const displayableBefore = countDisplayableMessages()

      await hooks?.beforeHistoryLoad?.()

      if (isDisposed) {
        return
      }

      if (needsNetwork) {
        startLoading()
      }

      let appendedCount = 0
      let didPreparePrepend = false
      let fetchedHistory: HistoryMessageItem[] = []
      try {
        if (cached?.length) {
          fetchedHistory = cached
        } else {
          fetchedHistory = await fetchHistory(pServed, LAZY_LOAD_HISTORY_BATCH_SIZE)
        }

        if (isDisposed) {
          return
        }

        if (hooks?.canLoadOlderHistory && !hooks.canLoadOlderHistory()) {
          return
        }

        appendedCount = countNewHistoryMessages(fetchedHistory)
        if (appendedCount > 0) {
          // Virtua Chat pattern: lock + shift (auto) in the same turn as addMessages.
          hooks?.preparePrepend?.()
          didPreparePrepend = true
        }
        appendHistory(fetchedHistory)
        if (appendedCount > 0) {
          hooks?.onHistoryAppended?.()
        }
      } finally {
        if (needsNetwork) {
          stopLoading()
        }
      }

      const displayableAfter = countDisplayableMessages()
      const addedVisibleMessages = displayableAfter > displayableBefore

      if (appendedCount > 0 && addedVisibleMessages) {
        startCoolDown(() => {
          hooks?.onLoadCooldownEnd?.()
        })
      }

      await nextTick()
      if (isDisposed) {
        return
      }
      if (didPreparePrepend) {
        await hooks?.afterHistoryLoad?.()
      }

      if (
        appendedCount > 0
        && !addedVisibleMessages
        && hasMoreHistory()
        && invisibleChain < MAX_INVISIBLE_HISTORY_CHAIN
      ) {
        await loadHistory(invisibleChain + 1)
        return
      }

      if (hasMoreHistory() && appendedCount > 0 && !addedVisibleMessages) {
        hooks?.onLoadCooldownEnd?.()
      }
    }

    return loadHistory().finally(() => {
      isLoadInFlight = false
    })
  }

  const enable = () => {
    if (isDisposed || isEnabled.value || disableHistoryLoading) {
      return
    }
    // Prefetch early while scrolling; insert only when gesture settles or hard top edge.
    emitter.on('scrolledNearTop', prefetchEvents)
    emitter.on('scrolledToTop', loadEvents)
    emitter.on('scrollStop', loadEvents)
    emitter.on('userAtChatBottom', clearPendingHistory)
    isEnabled.value = true
  }

  const disable = () => {
    if (isEnabled.value) {
      emitter.off('scrolledNearTop', prefetchEvents)
      emitter.off('scrolledToTop', loadEvents)
      emitter.off('scrollStop', loadEvents)
      emitter.off('userAtChatBottom', clearPendingHistory)
      isEnabled.value = false
    }
    if (coolDownTimer) {
      clearTimeout(coolDownTimer)
      coolDownTimer = null
    }
    coolDown.value = false
    clearPendingHistory()
  }

  onScopeDispose(() => {
    isDisposed = true
    disable()
  })

  return {
    enable,
    disable,
    clearPendingHistory,
  }
}
